/**
 * Phase 3 · Purchase: requisitions → purchase orders → GRN → invoices,
 * supplier returns, ledger and price history.
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus, Send, Truck } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { money, num, dateTime } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input, Select } from '../components/ui/Input';

interface Row { id: string; [k: string]: unknown }
interface Supplier extends Row { name: string; contact_person: string | null; phone: string | null }
interface Ingredient { id: string; name: string; unit_symbol: string; unit_cost: number }

const TABS = ['Orders', 'Requests', 'Invoices', 'Suppliers'] as const;

export function PurchasePage() {
  const can = useAuth((s) => s.user?.permissions.includes('purchase:order') ?? false);
  const canInvoice = useAuth((s) => s.user?.permissions.includes('purchase:invoice') ?? false);
  const toast = useUi((s) => s.toast);
  const [tab, setTab] = useState<(typeof TABS)[number]>('Orders');
  const [orders, setOrders] = useState<Row[]>([]);
  const [requests, setRequests] = useState<Row[]>([]);
  const [invoices, setInvoices] = useState<Row[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [poModal, setPoModal] = useState(false);
  const [reqModal, setReqModal] = useState(false);
  const [invModal, setInvModal] = useState(false);
  const [grnFor, setGrnFor] = useState<Row | null>(null);
  const [ledgerFor, setLedgerFor] = useState<Supplier | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, r, i, s, ing] = await Promise.all([
        http.get<{ orders: Row[] }>('/purchase/orders'),
        http.get<{ requests: Row[] }>('/purchase/requests'),
        http.get<{ invoices: Row[] }>('/purchase/invoices').catch(() => ({ invoices: [] })),
        http.get<{ suppliers: Supplier[] }>('/inventory/suppliers'),
        http.get<{ ingredients: Ingredient[] }>('/inventory/ingredients'),
      ]);
      setOrders(o.orders); setRequests(r.requests); setInvoices(i.invoices);
      setSuppliers(s.suppliers); setIngredients(ing.ingredients);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast({ kind: 'success', title: ok }); await load(); }
    catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Purchasing</h1>
          <p className="text-sm text-ink-muted">PR → PO → GRN → invoice, with returns and ledgers</p>
        </div>
        <div className="flex gap-2">
          {can && <Button variant="secondary" onClick={() => setReqModal(true)}><Plus size={16} /> Requisition</Button>}
          {can && <Button onClick={() => setPoModal(true)}><Plus size={16} /> Purchase order</Button>}
        </div>
      </header>

      <div className="flex flex-wrap gap-1 rounded-lg bg-surface-1 p-1" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === t ? 'bg-surface-0 text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Orders' && (
        <DataTable columns={[
          { key: 'po_number', header: 'PO', render: (r: Row) => String(r.po_number ?? r.reference ?? r.id.slice(0, 8)) },
          { key: 'supplier_name', header: 'Supplier' },
          { key: 'status', header: 'Status', render: (r: Row) => <StatusBadge status={String(r.status)} /> },
          { key: 'ordered_qty', header: 'Received', render: (r: Row) => `${num(r.received_qty as number, 1)} / ${num(r.ordered_qty as number, 1)}` },
          { key: 'total', header: 'Total', render: (r: Row) => money(r.total as number) },
          { key: 'expected_date', header: 'Expected', render: (r: Row) => String(r.expected_date ?? '—') },
          ...(can ? [{
            key: 'act', header: '', render: (r: Row) => (
              <div className="flex justify-end gap-2">
                {r.status === 'draft' && <Button size="sm" variant="secondary" onClick={() => void act(() => http.post(`/purchase/orders/${r.id}/send`), 'PO sent')}><Send size={13} /> Send</Button>}
                {['sent', 'partial'].includes(String(r.status)) && <Button size="sm" variant="secondary" onClick={() => setGrnFor(r)}><Truck size={13} /> Receive</Button>}
              </div>
            ),
          }] as Column<Row>[] : []),
        ]} rows={orders} loading={loading} />
      )}

      {tab === 'Requests' && (
        <DataTable columns={[
          { key: 'created_at', header: 'Raised', render: (r: Row) => dateTime(String(r.created_at)) },
          { key: 'requested_by_name', header: 'By' },
          { key: 'priority', header: 'Priority', render: (r: Row) => <Badge tone={r.priority === 'urgent' ? 'danger' : r.priority === 'low' ? 'neutral' : 'info'}>{String(r.priority)}</Badge> },
          { key: 'item_count', header: 'Lines' },
          { key: 'status', header: 'Status', render: (r: Row) => <StatusBadge status={String(r.status)} /> },
          ...(can ? [{
            key: 'act', header: '', render: (r: Row) => r.status === 'pending'
              ? <div className="flex justify-end"><Button size="sm" variant="secondary" onClick={() => void act(() => http.post(`/purchase/requests/${r.id}/approve`), 'Approved')}>Approve</Button></div>
              : null,
          }] as Column<Row>[] : []),
        ]} rows={requests} loading={loading} />
      )}

      {tab === 'Invoices' && (
        <>
          {canInvoice && <div className="flex justify-end"><Button variant="secondary" onClick={() => setInvModal(true)}><Plus size={16} /> Invoice</Button></div>}
          <DataTable columns={[
            { key: 'invoice_no', header: 'Invoice' },
            { key: 'supplier_name', header: 'Supplier' },
            { key: 'invoice_date', header: 'Date', render: (r: Row) => String(r.invoice_date) },
            { key: 'total', header: 'Total', render: (r: Row) => money(r.total as number) },
            { key: 'paid', header: 'Paid', render: (r: Row) => money(r.paid as number) },
            { key: 'outstanding', header: 'Outstanding', render: (r: Row) => Number(r.outstanding) > 0 ? <Badge tone="warning">{money(r.outstanding as number)}</Badge> : <Badge tone="success">Settled</Badge> },
            ...(canInvoice ? [{
              key: 'act', header: '', render: (r: Row) => Number(r.outstanding) > 0
                ? <div className="flex justify-end"><Button size="sm" variant="secondary" onClick={() => void act(() => http.post(`/purchase/invoices/${r.id}/pay`, { amount: Number(r.outstanding) }), 'Payment recorded')}>Pay</Button></div>
                : null,
            }] as Column<Row>[] : []),
          ]} rows={invoices} loading={loading} />
        </>
      )}

      {tab === 'Suppliers' && (
        <DataTable columns={[
          { key: 'name', header: 'Supplier', sortable: true },
          { key: 'contact_person', header: 'Contact', render: (r: Supplier) => r.contact_person ?? '—' },
          { key: 'phone', header: 'Phone', render: (r: Supplier) => r.phone ?? '—' },
          { key: 'act', header: '', render: (r: Supplier) => <div className="flex justify-end"><Button size="sm" variant="ghost" onClick={() => setLedgerFor(r)}>Ledger</Button></div> },
        ]} rows={suppliers} loading={loading} />
      )}

      {poModal && <PoModal suppliers={suppliers} ingredients={ingredients} onClose={() => setPoModal(false)} onSaved={() => { setPoModal(false); void load(); }} />}
      {reqModal && <RequestModal ingredients={ingredients} onClose={() => setReqModal(false)} onSaved={() => { setReqModal(false); void load(); }} />}
      {invModal && <InvoiceModal suppliers={suppliers} onClose={() => setInvModal(false)} onSaved={() => { setInvModal(false); void load(); }} />}
      {grnFor && <GrnModal po={grnFor} onClose={() => setGrnFor(null)} onSaved={() => { setGrnFor(null); void load(); }} />}
      {ledgerFor && <LedgerModal supplier={ledgerFor} onClose={() => setLedgerFor(null)} />}
    </div>
  );
}

interface Line { ingredientId: string; quantity: string; unit: string; unitCost: string }

function LinesEditor({ lines, setLines, ingredients, withCost }: {
  lines: Line[]; setLines: (fn: (l: Line[]) => Line[]) => void; ingredients: Ingredient[]; withCost?: boolean;
}) {
  return (
    <div className="space-y-2">
      {lines.map((l, i) => (
        <div key={i} className={`grid gap-2 ${withCost ? 'grid-cols-[1fr_5rem_5rem_6rem_2.5rem]' : 'grid-cols-[1fr_5rem_5rem_2.5rem]'}`}>
          <Select aria-label="Ingredient" value={l.ingredientId} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, ingredientId: e.target.value, unit: ingredients.find((g) => g.id === e.target.value)?.unit_symbol ?? x.unit } : x))}>
            {ingredients.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Select>
          <Input aria-label="Quantity" type="number" step="any" min="0" value={l.quantity} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} />
          <Input aria-label="Unit" value={l.unit} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} />
          {withCost && <Input aria-label="Unit cost (£)" type="number" step="0.01" min="0" value={l.unitCost} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, unitCost: e.target.value } : x))} />}
          <Button size="sm" variant="ghost" aria-label="Remove" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>✕</Button>
        </div>
      ))}
      <Button size="sm" variant="secondary" onClick={() => setLines((ls) => [...ls, { ingredientId: ingredients[0]?.id ?? '', quantity: '1', unit: ingredients[0]?.unit_symbol ?? 'pcs', unitCost: ((Number(ingredients[0]?.unit_cost ?? 0)) / 100).toFixed(2) }])}>
        <Plus size={14} /> Add line
      </Button>
    </div>
  );
}

function PoModal({ suppliers, ingredients, onClose, onSaved }: { suppliers: Supplier[]; ingredients: Ingredient[]; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? '');
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post('/purchase/orders', {
        supplierId,
        items: lines.map((l) => ({ ingredientId: l.ingredientId, quantity: parseFloat(l.quantity || '0'), unitSymbol: l.unit, unitCost: Math.round(parseFloat(l.unitCost || '0') * 100) })),
      });
      toast({ kind: 'success', title: 'Purchase order created' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New purchase order" size="lg"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Create</Button></>}>
      <div className="mb-3"><Select label="Supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></div>
      <LinesEditor lines={lines} setLines={setLines} ingredients={ingredients} withCost />
    </Modal>
  );
}

function RequestModal({ ingredients, onClose, onSaved }: { ingredients: Ingredient[]; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [priority, setPriority] = useState<'low' | 'normal' | 'urgent'>('normal');
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post('/purchase/requests', { priority, items: lines.map((l) => ({ ingredientId: l.ingredientId, quantity: parseFloat(l.quantity || '0'), unitSymbol: l.unit })) });
      toast({ kind: 'success', title: 'Requisition raised' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New purchase requisition" size="lg"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Raise</Button></>}>
      <div className="mb-3"><Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value as never)}><option>low</option><option>normal</option><option>urgent</option></Select></div>
      <LinesEditor lines={lines} setLines={setLines} ingredients={ingredients} />
    </Modal>
  );
}

function InvoiceModal({ suppliers, onClose, onSaved }: { suppliers: Supplier[]; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? '');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [subtotal, setSubtotal] = useState('');
  const [tax, setTax] = useState('0');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post('/purchase/invoices', {
        supplierId, invoiceNo: invoiceNo || `INV-${Date.now() % 100000}`, invoiceDate: date,
        subtotal: Math.round(parseFloat(subtotal || '0') * 100), tax: Math.round(parseFloat(tax || '0') * 100),
      });
      toast({ kind: 'success', title: 'Invoice recorded' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Record supplier invoice"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Save</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select label="Supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
        <Input label="Invoice no." value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
        <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Input label="Subtotal (£)" type="number" step="0.01" value={subtotal} onChange={(e) => setSubtotal(e.target.value)} />
        <Input label="Tax (£)" type="number" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} />
      </div>
    </Modal>
  );
}

function GrnModal({ po, onClose, onSaved }: { po: Row; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [items, setItems] = useState<{ poItemId: string; ingredientId: string; name: string; ordered: number; unit: string; receive: string; cost: string }[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void http.get<{ order: Row; items: Row[] }>(`/purchase/orders/${po.id}`).then((r) => {
      setItems((r.items as Row[]).map((i) => ({
        poItemId: String(i.id), ingredientId: String(i.ingredient_id ?? ''), name: String(i.ingredient_name ?? i.name ?? ''), ordered: Number(i.quantity),
        unit: String(i.unit_symbol), receive: String(Number(i.quantity) - Number(i.received_qty ?? 0)),
        cost: String(Number(i.unit_cost ?? 0) / 100),
      })));
      setLoaded(true);
    });
  }, [po.id]);

  const save = async () => {
    setBusy(true);
    try {
      await http.post('/purchase/grns', {
        poId: po.id,
        items: items.map((i) => ({ poItemId: i.poItemId, ingredientId: i.ingredientId, quantity: parseFloat(i.receive || '0'), unitSymbol: i.unit, unitCost: Math.round(parseFloat(i.cost || '0') * 100) })),
      });
      toast({ kind: 'success', title: 'GRN posted — stock & costs updated' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Receive against ${String(po.po_number ?? 'PO')}`} size="lg"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Post GRN</Button></>}>
      {!loaded ? <p className="text-sm text-ink-muted">Loading…</p> : (
        <div className="space-y-2">
          {items.map((i, idx) => (
            <div key={i.poItemId} className="grid grid-cols-[1fr_5rem_5rem_6rem] items-center gap-2 text-sm">
              <span className="truncate text-ink">{i.name} <span className="text-xs text-ink-muted">(ordered {num(i.ordered, 1)} {i.unit})</span></span>
              <Input aria-label="Receive qty" type="number" step="any" min="0" value={i.receive} onChange={(e) => setItems((xs) => xs.map((x, j) => j === idx ? { ...x, receive: e.target.value } : x))} />
              <span className="text-xs text-ink-muted">{i.unit}</span>
              <Input aria-label="Unit cost" type="number" step="0.01" min="0" value={i.cost} onChange={(e) => setItems((xs) => xs.map((x, j) => j === idx ? { ...x, cost: e.target.value } : x))} />
            </div>
          ))}
          <p className="text-xs text-ink-muted">Received cost re-prices stock and re-costs any recipe using the ingredient.</p>
        </div>
      )}
    </Modal>
  );
}

function LedgerModal({ supplier, onClose }: { supplier: Supplier; onClose: () => void }) {
  const [entries, setEntries] = useState<Row[]>([]);
  const [balance, setBalance] = useState(0);
  useEffect(() => {
    void http.get<{ entries: Row[]; balance: number }>(`/purchase/suppliers/${supplier.id}/ledger`).then((r) => {
      setEntries(r.entries); setBalance(r.balance);
    });
  }, [supplier.id]);
  return (
    <Modal open onClose={onClose} title={`${supplier.name} — ledger`} size="lg"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      <p className="mb-3 text-sm text-ink-muted">Outstanding balance: <strong className="text-ink">{money(balance)}</strong></p>
      <DataTable columns={[
        { key: 'created_at', header: 'When', render: (r: Row) => dateTime(String(r.created_at)) },
        { key: 'entry_type', header: 'Type', render: (r: Row) => String(r.entry_type) },
        { key: 'reference', header: 'Ref', render: (r: Row) => String(r.reference ?? '—') },
        { key: 'amount', header: 'Amount', render: (r: Row) => money(r.amount as number) },
        { key: 'balance', header: 'Balance', render: (r: Row) => money(r.balance as number) },
      ]} rows={entries} />
    </Modal>
  );
}
